/**
 * Компонент авторизации с поддержкой Google OAuth и Magic Link
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TelegramAuth from './TelegramAuth';
import AccountLinking from './AccountLinking';
import { shouldRedirectToGame } from '../utils/authRedirect';

// RedTrack rt_clickid автосохранение
function useRtClickId() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rtClickId = urlParams.get('rt_clickid');

    if (rtClickId) {
      localStorage.setItem('rt_clickid', rtClickId);
      console.log('✅ rt_clickid автоматически сохранен в Auth:', rtClickId.substring(0,8));
    }
  }, []);
}

interface AuthProps {
  onClose?: () => void;
  variant?: 'a' | 'b';
}

const Auth: React.FC<AuthProps> = ({ onClose, variant = 'a' }) => {
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithEmail, signInWithTelegram, error, loading, user } = useAuth();

  // Автоматическое сохранение rt_clickid из URL
  useRtClickId();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAccountLinking, setShowAccountLinking] = useState(false);
  const [telegramData, setTelegramData] = useState<any>(null);

  const handleGoogleSignIn = async () => {
    // Сохраняем guest_id и game_id перед redirect на Google OAuth
    const guest_id = localStorage.getItem('guest_id');
    const guest_game_id = localStorage.getItem('guest_game_id');

    if (guest_id && guest_game_id) {
      // Сохраняем во временное хранилище для восстановления после OAuth redirect
      sessionStorage.setItem('pending_guest_link', JSON.stringify({ guest_id, guest_game_id }));
      console.log('💾 Saved pending guest link for Google OAuth');
    }

    // Google OAuth redirect обрабатывается в AuthCallback.tsx
    const { error } = await signInWithGoogle();
    if (error) {
      console.error('Google sign in error:', error);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Получаем guest_id и game_id для передачи через URL
    const guest_id = localStorage.getItem('guest_id');
    const guest_game_id = localStorage.getItem('guest_game_id');

    setIsSubmitting(true);
    const { error } = await signInWithEmail(email.trim(), guest_id, guest_game_id);
    setIsSubmitting(false);

    if (!error) {
      setEmailSent(true);
    } else {
      console.error('Email sign in error:', error);
    }
  };

  const handleTelegramAuth = async (telegramData: any) => {
    // Получаем guest_id из localStorage если есть
    const guest_id = localStorage.getItem('guest_id');
    const guest_game_id = localStorage.getItem('guest_game_id');

    console.log('🔐 Auth: guest_id=', guest_id, 'guest_game_id=', guest_game_id);

    const result = await signInWithTelegram(telegramData, guest_id || undefined);

    if (result.error) {
      console.error('Telegram sign in error:', result.error);
    } else if (result.needsLinking) {
      // Показываем модал для связывания аккаунтов
      setTelegramData(telegramData);
      setShowAccountLinking(true);
    } else {
      // Успешная авторизация - закрываем модальное окно и редиректим
      console.log('Telegram auth successful');
      if (onClose) {
        onClose();
      }

      // Проверяем есть ли guest game для продолжения
      if (guest_game_id) {
        console.log('✅ Found guest game, redirecting to continue game:', guest_game_id);
        // Очищаем guest данные из localStorage
        localStorage.removeItem('guest_id');
        localStorage.removeItem('guest_game_id');
        // Redirect на игру с gameId параметром
        window.location.href = `/?gameId=${guest_game_id}`;
        return;
      }

      // Обычный flow: проверяем количество игр для определения redirect
      setTimeout(async () => {
        const isNewUser = await shouldRedirectToGame();
        const playerName = result.user?.display_name || result.user?.email?.split('@')[0] || 'Игрок';

        if (isNewUser) {
          // Новый пользователь - сразу в игру
          console.log('New user detected, redirecting to game with player:', playerName);
          navigate(`/magic?playerName=${encodeURIComponent(playerName)}`);
        } else {
          // Возвращающийся пользователь - на список игр
          console.log('Returning user detected, redirecting to my-games');
          navigate('/my-games');
        }
      }, 100);
    }
  };

  const handleAccountLinkingSuccess = (user: any) => {
    setShowAccountLinking(false);
    setTelegramData(null);
    if (onClose) onClose();
  };

  const handleAccountLinkingCancel = () => {
    setShowAccountLinking(false);
    setTelegramData(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
      </div>
    );
  }

  if (emailSent) {
    return (
      <div className="text-center p-4">
        <div className="mb-3">
          <i className="bi bi-envelope-check text-success" style={{ fontSize: '3rem' }}></i>
        </div>
        <h4 className="text-success mb-3">Письмо отправлено!</h4>
        <p className="text-muted mb-4">
          Мы отправили вам ссылку для входа на <strong>{email}</strong>
        </p>
        <p className="small text-muted">
          Проверьте почту и перейдите по ссылке для входа в систему
        </p>
        {onClose && (
          <button 
            className="btn btn-outline-secondary mt-3" 
            onClick={onClose}
          >
            Закрыть
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="auth-container p-4">
      <div className="text-center mb-4">
        {variant === 'b' ? (
          <>
            <h3 className="mb-3">🔮 Начни путешествие внутрь себя за 5 секунд</h3>
            <p className="text-muted">
              Без регистрации и паролей — просто выбери способ и играй.<br/>
              Твоя история начинается с одного шага.
            </p>
          </>
        ) : (
          <>
            <h3 className="mb-3">🚀 Вход в игру за 5 секунд</h3>
            <p className="text-muted">Без пароля — просто выбери удобный способ и начинай</p>
          </>
        )}
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <small>{error}</small>
        </div>
      )}

      {/* Google OAuth */}
      <div className="d-grid gap-2 mb-3">
        <button
          className="btn btn-outline-danger d-flex align-items-center justify-content-center"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <svg className="me-2" width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {variant === 'b' ? 'Играть через Google' : 'Войти через Google'}
        </button>
      </div>

      {/* Telegram Auth */}
      <div className="d-grid gap-2 mb-3 text-center">
        {(() => {
          console.log('Auth.tsx: About to render TelegramAuth component');
          return null;
        })()}
        <TelegramAuth 
          onSuccess={handleTelegramAuth}
          onError={(error) => console.error('Telegram auth error:', error)}
          onLinkAccount={handleTelegramAuth}
        />
      </div>

      <div className="text-center mb-3">
        <small className="text-muted">или</small>
      </div>

      {/* Email Magic Link */}
      <form onSubmit={handleEmailSignIn}>
        <div className="mb-3">
          <label htmlFor="email" className="form-label">Email адрес</label>
          <input
            type="email"
            className="form-control"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            disabled={isSubmitting}
          />
          <div className="form-text">
            {variant === 'b'
              ? 'Мы пришлём ссылку для старта игры'
              : 'Мы отправим вам ссылку для входа без пароля'
            }
          </div>
        </div>
        <div className="d-grid gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !email.trim()}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Отправляем...
              </>
            ) : (
              <>
                <i className="bi bi-envelope me-2"></i>
                {variant === 'b' ? 'Получить ссылку на игру' : 'Отправить ссылку'}
              </>
            )}
          </button>
        </div>
      </form>

      {onClose && (
        <div className="text-center mt-3">
          <button
            className="btn btn-link text-muted"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      )}

      {/* Variant B: Additional motivational text */}
      {variant === 'b' && (
        <>
          <div className="text-center mt-4 pt-4 border-top">
            <p className="text-muted small mb-0">
              После входа ты сразу попадёшь на игровое поле<br/>
              и бросишь первый кубик 🎲
            </p>
          </div>
          <div className="text-center mt-3 p-3" style={{ background: 'rgba(102, 126, 234, 0.05)', borderRadius: '12px' }}>
            <p className="mb-0 fw-semibold" style={{ fontSize: '15px', color: '#2d3748' }}>
              🧠 Лила Онлайн — это не просто игра.<br/>
              Это зеркало твоего внутреннего пути.
            </p>
          </div>
        </>
      )}

      {/* Account Linking Modal */}
      {showAccountLinking && telegramData && (
        <AccountLinking
          telegramData={telegramData}
          currentUser={null} // TODO: получить текущего пользователя
          onSuccess={handleAccountLinkingSuccess}
          onCancel={handleAccountLinkingCancel}
        />
      )}
    </div>
  );
};

export default Auth;
